import {
  AnalyticsSettings,
  logEvent as firebaseLogEvent,
  setUserId as firebaseSetUserId,
  setUserProperties as firebaseSetUserProperties,
  initializeAnalytics,
  setDefaultEventParameters,
} from 'firebase/analytics';
import { getApps, initializeApp } from 'firebase/app';
import { Messaging, Unsubscribe } from 'firebase/messaging';

import { getEnv } from '@/constants/env';
import Airbridge from '@/utils/airbridge';
import { infoReport } from '@/utils/report';

import { Client } from '@/types/app';
import { getErrorObject } from './error';
import { isMobileWebByUserAgent, isWebViewByUserAgent } from './mobile/RNPlugins';

enum SERVICE_PLATFORM {
  WEB = 'web',
  USER_WEB = 'user_web',
}

enum SERVICE_UA_CATEGORY {
  DEVICE = 'device',
  BROWSER = 'browser',
}

enum DEVICE_PLATFORM {
  PC_WEB = 'pc_web',
  MOBILE_WEB = 'mobile_web',
  WEB_VIEW = 'web_view',
}

class Firebase {
  private static _analytics: Messaging | undefined;
  private static _unsubscribe: Unsubscribe | undefined;
  public static token: string | undefined;

  static hasInitializedApp() {
    try {
      return getApps().length > 0;
    } catch (error) {
      infoReport('Firebase:hasInitializedApp > error', {}, getErrorObject(error));
      return false;
    }
  }

  static hasFullyInitialized() {
    try {
      return getApps().length > 0 && this._analytics !== undefined;
    } catch (error) {
      infoReport('Firebase:hasFullyInitialized > error', {}, getErrorObject(error));
      return false;
    }
  }

  static initApp(userAgent: string, client: Client) {
    if (this.hasFullyInitialized()) {
      return;
    }

    try {
      let webApp;
      if (this.hasInitializedApp()) {
        const apps = getApps();
        webApp = apps.find((app) => app.name === 'doctornow-web');

        if (!webApp) {
          throw new Error('기존 Firebase 앱을 찾을 수 없습니다');
        }
      } else {
        webApp = initializeApp(
          {
            apiKey: 'AIzaSyC1Te_JUAD_peZJg-8gWAVIowC3Qv9VRBA',
            authDomain: 'doctornow-web.firebaseapp.com',
            databaseURL: 'https://baedalyakgook.firebaseio.com',
            projectId: 'doctornow-web',
            storageBucket: 'doctornow-web.appspot.com',
            messagingSenderId: '512351040521',
            appId: '1:512351040521:web:cbcce24e6e7619fd9d74d8',
            measurementId: 'G-8NECV1E3F0',
          },
          'doctornow-web'
        );
      }

      const analyticsConfig: AnalyticsSettings = {
        config: { send_page_view: false },
      };

      Airbridge.initialize();
      Airbridge.setClient(client);

      this._analytics = initializeAnalytics(webApp, analyticsConfig);

      const environmentProperties = this.getUserServiceEnvironmentProperty(userAgent);

      this.setUserProperties(environmentProperties);
      this.setDefaultEventParameters(environmentProperties);
    } catch (error) {
      infoReport('Firebase:initApp > error', {}, getErrorObject(error));
    }
  }

  static removeListener() {
    if (!this._unsubscribe) return;
    this._unsubscribe();
  }

  static logEventOnlyFirebase(eventName: string, eventParams?: Record<string, unknown>) {
    try {
      if (!this._analytics) return;
      firebaseLogEvent(this._analytics, eventName, eventParams);
    } catch (error) {
      infoReport(
        'Firebase:logEvent > error',
        {
          error,
          eventName,
          eventParams,
        },
        getErrorObject(error)
      );
    }
  }

  static logEvent(eventName: string, eventParams?: Record<string, unknown>) {
    try {
      if (!this._analytics) return;
      firebaseLogEvent(this._analytics, eventName, eventParams);
      Airbridge.trackEvent(eventName, eventParams);
    } catch (error) {
      infoReport(
        'Firebase:logEvent > error',
        {
          error,
          eventName,
          eventParams,
        },
        getErrorObject(error)
      );
    }
  }

  static setUserProperties(properties: Record<string, string>) {
    try {
      if (!this._analytics) return;
      firebaseSetUserProperties(this._analytics, { ...properties });
      Airbridge.setUserProperties(properties);
    } catch (error) {
      infoReport('Firebase:setUserProperties > error', { properties }, getErrorObject(error));
    }
  }

  static setUserId(id: string) {
    try {
      if (!this._analytics) return;
      firebaseSetUserId(this._analytics, id);
      Airbridge.setUserId(id);
    } catch (error) {
      infoReport('Firebase:setUserId > error', { userId: id }, getErrorObject(error));
    }
  }

  static setDefaultEventParameters(properties: Record<string, string>) {
    try {
      if (!this._analytics) return;
      setDefaultEventParameters({ ...properties });
    } catch (error) {
      infoReport('Firebase:setDefaultEventParameters > error', {}, getErrorObject(error));
    }
  }

  static getMobileWebUserServiceEnvironmentProperty() {
    return {
      service_platform: SERVICE_PLATFORM.USER_WEB,
      service_ua_category: SERVICE_UA_CATEGORY.BROWSER,
      device_platform: DEVICE_PLATFORM.MOBILE_WEB,
      env: getEnv(),
    };
  }

  static getUserServiceEnvironmentProperty(userAgent: string) {
    if (isWebViewByUserAgent(userAgent)) {
      // 웹뷰
      return {
        service_platform: SERVICE_PLATFORM.USER_WEB,
        service_ua_category: SERVICE_UA_CATEGORY.DEVICE,
        device_platform: DEVICE_PLATFORM.WEB_VIEW,
        env: getEnv(),
      };
    }
    if (isMobileWebByUserAgent(userAgent)) {
      // 모바일 웹
      return {
        service_platform: SERVICE_PLATFORM.USER_WEB,
        service_ua_category: SERVICE_UA_CATEGORY.BROWSER,
        device_platform: DEVICE_PLATFORM.MOBILE_WEB,
        env: getEnv(),
      };
    }
    // 웹
    return {
      service_platform: SERVICE_PLATFORM.WEB,
      service_ua_category: SERVICE_UA_CATEGORY.BROWSER,
      device_platform: DEVICE_PLATFORM.PC_WEB,
      env: getEnv(),
    };
  }
}

export default Firebase;
